import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import SelectBox from 'devextreme-react/select-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  StorageMountsAdminPage as PlatformStorageMountsAdminPage,
  type StorageMountsAdminApi,
  type StorageMountsAdminCopy
} from '@jarvis/platform-ui';
import {
  createClientPortalStorageMountsApi,
  getClientPortalGraphReadiness,
  listClientPortalTenants,
  type ClientGraphReadinessReport,
  type ClientPortalTenantOption
} from '../../../api/clientPortalStorageMountsAdmin';

const clientPortalCopy: StorageMountsAdminCopy = {
  apiSiteLabel: 'Client Portal API',
  pageSubtitle:
    'Configure SMB shares and S3/MinIO buckets for client portal Secure Pharm Reports. Assign SPR roles and client-tenant Entra group bindings to control per-mount visibility.',
  noBindingsHint: 'No bindings — only SPR-Admin and global override groups can access this mount.',
  accessGrantHint:
    'Grant an SPR role (+ client-tenant Entra group) read or write access. SPR-Admin always has full access to all mounts.',
  adminRoleLabel: 'SPR-Admin'
};

function graphStatusClass(
  probeSucceeded: boolean | null | undefined,
  credentialsComplete: boolean
): string {
  if (probeSucceeded === true) return 'storage-graph-status-card__status--ready';
  if (probeSucceeded === false || !credentialsComplete) return 'storage-graph-status-card__status--failed';
  return 'storage-graph-status-card__status--muted';
}

function GraphReadinessCard({ tenantId }: { tenantId: string }) {
  const [graphReport, setGraphReport] = useState<ClientGraphReadinessReport | null>(null);
  const [graphLoading, setGraphLoading] = useState(true);
  const [graphError, setGraphError] = useState<string | null>(null);

  const loadGraph = useCallback(() => {
    setGraphLoading(true);
    getClientPortalGraphReadiness(tenantId, true)
      .then(setGraphReport)
      .catch((e: unknown) => setGraphError(e instanceof Error ? e.message : String(e)))
      .finally(() => setGraphLoading(false));
  }, [tenantId]);

  useEffect(() => {
    loadGraph();
  }, [loadGraph]);

  const selected = graphReport?.selectedTenant;

  return (
    <section className="storage-graph-status-card">
      <div className="storage-graph-status-card__header">
        <div>
          <strong>Client tenant Graph readiness</strong>
          <p className="storage-graph-status-card__hint">
            Group search for mount ACL bindings uses the Graph-only app for the selected tenant (`ClientGraph:Tenants`).
          </p>
        </div>
        <Button text="Re-check" icon="refresh" stylingMode="text" onClick={() => loadGraph()} disabled={graphLoading} />
      </div>
      {graphLoading ? (
        <div className="storage-graph-status-card__loading">
          <LoadIndicator visible height={18} width={18} />
          <span>Checking Graph configuration…</span>
        </div>
      ) : graphError ? (
        <div className="storage-graph-status-card__error">{graphError}</div>
      ) : selected ? (
        <div className="storage-graph-status-card__body">
          <div className="storage-graph-status-card__tenant-name">{selected.displayName}</div>
          <div className="storage-graph-status-card__message">
            <code className="storage-graph-status-card__tenant-id">{selected.tenantId}</code>
          </div>
          <div className={`storage-graph-status-card__status ${graphStatusClass(selected.probeSucceeded, selected.credentialsComplete)}`}>
            {selected.probeSucceeded === true
              ? 'Ready'
              : selected.probeSucceeded === false
                ? 'Probe failed'
                : selected.credentialsComplete
                  ? 'Configured'
                  : 'Incomplete'}
          </div>
          <div className="storage-graph-status-card__message">{selected.statusMessage}</div>
        </div>
      ) : (
        <span className="storage-graph-status-card__message">No Graph configuration found for the selected tenant.</span>
      )}
    </section>
  );
}

export function ClientPortalStorageMountsAdminPage() {
  const [tenants, setTenants] = useState<ClientPortalTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [tenantError, setTenantError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listClientPortalTenants();
        if (cancelled) return;
        setTenants(items);
        setSelectedTenantId(items[0]?.tenantId ?? null);
        if (items.length === 0) {
          setTenantError('No client tenants are configured. Add ClientGraph__Tenants__N__* to WebOps env.');
        }
      } catch (e: unknown) {
        if (!cancelled) setTenantError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoadingTenants(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const api = useMemo<StorageMountsAdminApi | null>(
    () => (selectedTenantId ? createClientPortalStorageMountsApi(selectedTenantId) : null),
    [selectedTenantId]
  );

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      <section style={{ padding: '0.5rem 0.75rem 0', maxWidth: 420, flexShrink: 0 }}>
        <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Client tenant</label>
        {loadingTenants ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <LoadIndicator visible height={20} width={20} />
            <span style={{ fontSize: 13 }}>Loading tenants…</span>
          </div>
        ) : tenantError ? (
          <div style={{ color: 'var(--dx-color-danger, #dc2626)', fontSize: 13 }}>{tenantError}</div>
        ) : (
          <SelectBox
            dataSource={tenants}
            displayExpr="displayName"
            valueExpr="tenantId"
            value={selectedTenantId}
            onValueChange={(value: string) => setSelectedTenantId(value)}
            width="100%"
          />
        )}
      </section>

      {api && selectedTenantId ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <PlatformStorageMountsAdminPage
            key={selectedTenantId}
            api={api}
            copy={clientPortalCopy}
            stateStorageKey={`webopsui:client-portal-storage-mounts:${selectedTenantId}`}
            headerSlot={<GraphReadinessCard tenantId={selectedTenantId} />}
          />
        </div>
      ) : null}
    </div>
  );
}
